import type { ApiErrorBody } from "./types";

/**
 * Thrown when the backend returns a non-2xx status. Preserves the status
 * code and the parsed ``detail`` message so callers can render them
 * without having to re-parse.
 */
export class ApiError extends Error {
	readonly status: number;
	readonly detail: string;

	constructor(status: number, detail: string) {
		super(`API ${status}: ${detail}`);
		this.status = status;
		this.detail = detail;
		this.name = "ApiError";
	}
}

async function parseError(response: Response): Promise<never> {
	let detail = response.statusText;
	try {
		const body = (await response.json()) as ApiErrorBody;
		if (typeof body.detail === "string") {
			detail = body.detail;
		}
	} catch {
		// Non-JSON response — fall back to statusText.
	}
	throw new ApiError(response.status, detail);
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
	const response = await fetch(path, {
		// Session-cookie auth: the backend sets the cookie on /auth/login
		// and every subsequent call carries it back. Same-origin in prod
		// (StaticFiles mount), proxied through Vite in dev.
		credentials: "include",
		headers: {
			"Content-Type": "application/json",
			Accept: "application/json",
			...(init.headers ?? {}),
		},
		...init,
	});

	if (!response.ok) {
		return parseError(response);
	}

	// 204 No Content (logout) — return empty object typed as T.
	if (response.status === 204) {
		return {} as T;
	}
	return (await response.json()) as T;
}

export const apiClient = {
	get: <T>(path: string) => request<T>(path, { method: "GET" }),
	post: <T>(path: string, body?: unknown) =>
		request<T>(path, {
			method: "POST",
			body: body === undefined ? undefined : JSON.stringify(body),
		}),
	put: <T>(path: string, body?: unknown) =>
		request<T>(path, {
			method: "PUT",
			body: body === undefined ? undefined : JSON.stringify(body),
		}),
	patch: <T>(path: string, body?: unknown) =>
		request<T>(path, {
			method: "PATCH",
			body: body === undefined ? undefined : JSON.stringify(body),
		}),
	delete: <T>(path: string) => request<T>(path, { method: "DELETE" }),
};
